import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Cache for stable URL generation - reuses same URL string for same inputs
// This ensures that the same video/image file gets the same URL across page navigations
const urlCache = new Map<string, string>();

export function withCacheBuster(
  url?: string | null,
  version?: string | number | null
): string {
  if (!url) return '';
  if (!version) return url;

  if (url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }

  // Create cache key from inputs
  const cacheKey = `${url}::${version}`;

  // Return cached URL if available (same string reference for video element comparison)
  const cached = urlCache.get(cacheKey);
  if (cached) {
    // console.log('[withCacheBuster] Cache HIT:', cacheKey.substring(0, 80));
    return cached;
  }

  // console.log('[withCacheBuster] Cache MISS, creating new URL:', cacheKey.substring(0, 80));
  const versionValue = String(version);
  const isAbsolute = /^https?:\/\//i.test(url) || url.startsWith('//');

  let result: string;

  try {
    const base = isAbsolute ? undefined : 'http://localhost';
    const parsed = new URL(url, base);
    parsed.searchParams.set('v', versionValue);
    result = parsed.toString();
    result = isAbsolute ? result : result.replace('http://localhost', '');
  } catch {
    const separator = url.includes('?') ? '&' : '?';
    result = `${url}${separator}v=${encodeURIComponent(versionValue)}`;
  }

  // Cache the result
  urlCache.set(cacheKey, result);

  // Limit cache size to prevent memory leaks (keep last 1000 URLs)
  if (urlCache.size > 1000) {
    // Remove oldest entries (first 200)
    const keysToDelete = Array.from(urlCache.keys()).slice(0, 200);
    keysToDelete.forEach(key => urlCache.delete(key));
  }

  return result;
}

// Prefix for optimistic, client-only slide page ids that have not been persisted
// to the database yet. These are NOT valid UUIDs, so any API call keyed on a real
// slide_page_id (text-strips, timelines, ...) must be skipped until the server
// returns the real id.
export const TEMP_PAGE_ID_PREFIX = 'temp-page-';

/**
 * Returns true when the slide page id refers to a real, persisted row (a UUID),
 * as opposed to an optimistic client-only id created before the server responds.
 */
export function isPersistedPageId(id?: string | null): id is string {
  return !!id && !id.startsWith(TEMP_PAGE_ID_PREFIX);
}

export function isLikelyVideoUrl(url?: string | null): boolean {
  if (!url) return false;

  try {
    const parsed = new URL(url, 'http://localhost');
    return /\.(mp4|webm|mov|m4v|avi|mkv)(?:$|\?)/i.test(parsed.pathname);
  } catch {
    return /\.(mp4|webm|mov|m4v|avi|mkv)(?:$|\?)/i.test(url);
  }
}

// This check can be removed, it is just for tutorial purposes
export const hasEnvVars =
  process.env.NEXT_PUBLIC_SUPABASE_URL &&
  process.env.NEXT_PUBLIC_SUPABASE_PUBLISHABLE_KEY;
